import moment from 'moment'
import videoButtonPlay from '../../../../../static/steam-img/video-button-play-normal.svg'
import { ModuleNames } from '../../../../constants/componentNames.constants'
import { generateBlogPostSlug } from '../../../../utils/blog.utils'
import { isResourceCategory } from '../../../../utils/functions.utils'
import { findTarget, getMicrocopyData } from '../../../../utils/microcopy.utils'

const resourcePageTypes = [
  ModuleNames.PAGE_CUSTOMER_STORY,
  ModuleNames.PAGE_ANALYST_REPORT,
  ModuleNames.PAGE_WHITE_PAPER,
  ModuleNames.PAGE_PLACEHOLDER,
  ModuleNames.PAGE_RESOURCE_DEMO,
]

export const getPageLink = (item: any) => {
  let pageLink = ''

  if (item.__typename === ModuleNames.TEASER) {
    if (item.url1) pageLink = item.url1
  } else if (item.__typename === ModuleNames.TOPIC_CARD || item.__typename === 'topicCard') {
    if (item.ctaLink) pageLink = item.ctaLink
  } else if (item.slug) {
    if (item.__typename === ModuleNames.PAGE_BLOG_POST) {
      pageLink = generateBlogPostSlug({
        slug: item.slug,
        blogCategoryName: item?.blogCategory?.name,
        pageLocale: item.entryLocale,
      })
    } else {
      pageLink = item.slug.charAt(0) === '/' ? item.slug : `/${item.slug}`
    }
  }

  return pageLink
}

export const getTimeAndCta = (itemData: any, resourceRowData: any) => {
  let convertedDate
  if (itemData.date) {
    convertedDate = new Date(itemData.date)
    itemData.date = moment(convertedDate).format('MMMM D, YYYY')
  }

  const datePassed = convertedDate && convertedDate < new Date()
  let cta, ctaText, when

  if (!resourceRowData.hideCtas) {
    cta = getMicrocopyData(
      resourceRowData.microcopyset,
      itemData.category.number,
      'primary',
      'text'
    )

    const isWebinar = isResourceCategory('Webinar', itemData.category.number)
    const isCategory = isResourceCategory('Events', itemData.category.number)

    if ((isWebinar || isCategory) && cta?.length >= 2 && convertedDate) {
      const now = new Date()
      when = convertedDate >= now ? 'before' : 'after'

      if (cta[0]?.key.includes(when)) {
        ctaText = cta[0].value
      } else if (cta[1]?.key.includes(when)) {
        ctaText = cta[1].value
      }
    }

    //* If not webinar, get the first cta text value
    if (!ctaText && cta.length && cta[0].value) ctaText = cta[0].value
  }

  return { ctaText, when, convertedDate, datePassed }
}

const getItemBody = (item: any) => {
  let body = ''

  if (item.__typename === ModuleNames.PAGE_BLOG_POST) {
    body = item.blogTeaserBody
  } else if (resourcePageTypes.indexOf(item.__typename) > -1) {
    body = item.teaserBody
  } else {
    body = item.body || item.description
  }

  if (!body) {
    if (item.seo?.pageMetaDescription) {
      body = item.seo.pageMetaDescription
    } else {
      body = item.seoMetadata?.pageMetaDescription
    }
  }

  return body
}

export const getCardDetails = (item: any, resourceRowData: any, isHomePage: boolean) => {
  if (!item) return {}
  const isResourceDemo = item.__typename === ModuleNames.PAGE_RESOURCE_DEMO
  const category = item.category || item.resourceCategory || {}

  const itemData = {
    category: {
      title: category?.title || '',
      number: category?.number || '',
      colorImage: {
        url: category?.colorImage?.image?.file?.url || '',
        alt: category?.colorImage?.altText || '',
      },
      tinyFeaturedIcon: {
        url: category?.tinyFeaturedIcon?.image?.file?.url || '',
        alt: category?.tinyFeaturedIcon?.altText || '',
      },
      whiteImage: {
        url: category?.whiteImage?.image?.file?.url || '',
        alt: category?.whiteImage?.altText || '',
      },
    },
    body: getItemBody(item),
    pageLink: getPageLink(item),
    inEnglish: item.inEnglish && !isHomePage ? item.inEnglish : false,
    inEnglishText: 'In English',
    image: {
      url: item.image?.image?.file?.url || item.mainImage?.image?.file?.url || '',
      altText: item.image?.altText || item.mainImage?.altText || '',
    },
    spotlight: {
      url: item.spotlight?.video?.image?.file?.url,
      altText: item.spotlight?.video?.altText,
    },
    companyLogo: {
      url: item.companyLogo?.image?.file?.url,
    },
    hero: {
      headline: item.hero?.headline,
    },
    date: item.date,
    tcEyebrow: item.eyebrow || '',
    ctaText: item.ctaText || '',
    tcVideo: {
      image: item.video?.image?.file?.url || '',
      altText: item.video?.altText || '',
      start: item.video?.start || '',
      animationFile: item.video?.animationFile?.file?.url || '',
      url: '',
      id: '',
      type: '',
    },
  }

  const assets = {
    image: {
      url: isResourceDemo ? itemData.spotlight.url : itemData.image.url,
      altText: isResourceDemo ? itemData.spotlight.altText : itemData.image.altText,
    },
    heading: isResourceDemo ? itemData.hero.headline : item.title || item.h1Title,
  }

  if (item.microcopy) {
    resourceRowData.microcopyset = item.microcopy
    itemData.inEnglishText = findTarget(`InEnglishPILL.text`, item.microcopy.values, false)
  }

  let categoryIcon = ''
  let companyLogo = ''
  let videoIcon = ''
  let companyAltText = ''
  let categoryAltText = ''

  //* category video demo
  if (!isHomePage) {
    if (isResourceCategory('Video Demo', itemData.category.number)) {
      videoIcon = itemData.category?.colorImage?.url || videoButtonPlay
    } else if (assets.image.url) {
      companyLogo = itemData.companyLogo?.url
      if (!companyLogo && itemData.category.whiteImage.url) {
        categoryIcon = itemData.category.whiteImage.url
        categoryAltText = itemData.category.whiteImage.alt
      } else {
        companyAltText = item.companyLogo && item.companyLogo.altText
      }
    } else {
      companyLogo = item.companyColorLogo?.image?.file?.url
      if (!companyLogo && itemData.category.colorImage.url) {
        categoryIcon = itemData.category.colorImage.url
        categoryAltText = itemData.category.colorImage.alt
      } else {
        companyAltText = item.companyColorLogo && item.companyColorLogo.altText
      }
    }
  }

  return { itemData, assets, categoryIcon, companyLogo, videoIcon, companyAltText, categoryAltText }
}
